/**
 * Reveal a section the first time it comes into view.
 *
 * Returns a ref to put on the element; the element gets `data-reveal="in"`
 * once it has been seen, and the stylesheet does the rest. Anything that
 * cannot be observed (an old browser, a test environment) is shown at once
 * rather than left invisible, and someone who asks for less motion is never
 * made to wait for an animation.
 *
 * Two things keep a section from sitting blank in front of a reader:
 *
 *   Anything already on screen when the hook mounts is revealed there and
 *   then, without waiting for the observer. That covers a reload partway
 *   down the page, a jump to #product from the nav, and a browser restoring
 *   its scroll position after the component mounts.
 *
 *   The margin grows the root downward instead of shrinking it, so a block
 *   starts fading in just before it reaches the viewport. The old value
 *   ended in -12%, which held a tall section invisible until it was well up
 *   the screen: the heading above it had been readable for a while by then,
 *   and the section under it looked empty.
 */

import { useEffect, useRef } from 'react'

export function useReveal<T extends HTMLElement = HTMLDivElement>(margin = '0px 0px 10% 0px') {
  const ref = useRef<T | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const show = () => { el.dataset.reveal = 'in' }

    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof IntersectionObserver !== 'function') {
      show()
      return
    }

    // Already in front of the reader: no animation to wait for.
    const box = el.getBoundingClientRect()
    if (box.top < window.innerHeight && box.bottom > 0) {
      show()
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          ;(entry.target as HTMLElement).dataset.reveal = 'in'
          observer.unobserve(entry.target)
        }
      },
      // Any part of it counts. A threshold of a few per cent never trips on a
      // section taller than the window until it is halfway past.
      { rootMargin: margin, threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [margin])

  return ref
}
